import { isRegisterStep } from './registration.model';
import type { FlowSession, FlowSessionSource } from './auth.types';

export const AUTH_STORAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const isTimestampExpired = (
  timestamp: number,
  maxAgeMs: number = AUTH_STORAGE_MAX_AGE_MS,
): boolean => {
  if (!Number.isFinite(timestamp) || timestamp <= 0) {
    return true;
  }

  return Date.now() - timestamp > maxAgeMs;
};

const FLOW_SESSION_STORAGE_KEY = 'new-registration-session-vm';

type StoredFlowSession = FlowSession & {
  updatedAt?: number;
};

const isWindowAvailable = (): boolean => {
  return typeof window !== 'undefined';
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return !!value && typeof value === 'object';
};

export const readFlowSession = (): FlowSession | null => {
  if (!isWindowAvailable()) {
    return null;
  }

  try {
    const serialized = localStorage.getItem(FLOW_SESSION_STORAGE_KEY);
    if (!serialized) {
      return null;
    }

    const parsed = JSON.parse(serialized) as unknown;
    if (!isRecord(parsed) || !isRegisterStep(parsed.step)) {
      return null;
    }

    const storedSession = parsed as StoredFlowSession;
    if (
      typeof storedSession.updatedAt !== 'number' ||
      isTimestampExpired(storedSession.updatedAt)
    ) {
      clearFlowSession();
      return null;
    }

    const source: FlowSessionSource =
      parsed.source === 'signin' ? 'signin' : 'signup';
    const email = typeof parsed.email === 'string' ? parsed.email : '';

    return {
      step: parsed.step,
      source,
      email,
    };
  } catch {
    return null;
  }
};

export const persistFlowSession = (session: FlowSession): void => {
  if (!isWindowAvailable()) {
    return;
  }

  try {
    localStorage.setItem(
      FLOW_SESSION_STORAGE_KEY,
      JSON.stringify({
        step: session.step,
        source: session.source,
        email: session.email,
        updatedAt: Date.now(),
      }),
    );
  } catch {
    return;
  }
};

export const clearFlowSession = (): void => {
  if (!isWindowAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(FLOW_SESSION_STORAGE_KEY);
  } catch {
    return;
  }
};

export const flowSessionStorageHelpers = {
  key: FLOW_SESSION_STORAGE_KEY,
};
